import React, { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { MessageSquare, Send, X, Minimize2, Maximize2, Circle } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const ChatBox = ({ rideId, currentUserId, otherUserName, otherUserRole }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const [ws, setWs] = useState(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  useEffect(() => {
    if (rideId && currentUserId) {
      fetchMessages();
      connectWebSocket();
    }

    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, [rideId, currentUserId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const connectWebSocket = () => {
    try {
      const token = localStorage.getItem('tekstar_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const wsUrl = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
      const wsConnection = new WebSocket(`${wsUrl}/api/chat/ws/${currentUserId}`);

      wsConnection.onopen = () => {
        console.log('WebSocket connected');
        setIsOnline(true);
      };

      wsConnection.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'message' && data.rideId === rideId) {
          const newMsg = {
            senderId: data.senderId,
            message: data.message,
            timestamp: data.timestamp
          };
          setMessages(prev => [...prev, newMsg]);
          
          if (!isOpen) {
            setUnreadCount(prev => prev + 1);
          }
        } else if (data.type === 'typing' && data.rideId === rideId) {
          setIsTyping(true);
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
          }
          typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 3000);
        }
      };

      wsConnection.onclose = () => {
        console.log('WebSocket disconnected');
        setIsOnline(false);
        // Try to reconnect after 3 seconds
        setTimeout(() => connectWebSocket(), 3000);
      };

      wsConnection.onerror = (error) => {
        console.error('WebSocket error:', error);
        setIsOnline(false);
      };

      setWs(wsConnection);
    } catch (error) {
      console.error('Error connecting WebSocket:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const api = (await import('../utils/api')).default;
      const response = await api.get(`/chat/messages/${rideId}`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    const messageText = newMessage.trim();
    setNewMessage('');

    try {
      if (ws && ws.readyState === WebSocket.OPEN) {
        // Send via WebSocket
        ws.send(JSON.stringify({
          rideId,
          message: messageText
        }));
        
        // Optimistically add message to UI
        setMessages(prev => [...prev, {
          senderId: currentUserId,
          message: messageText,
          timestamp: new Date().toISOString()
        }]);
      } else {
        // Fallback to REST API
        const api = (await import('../utils/api')).default;
        await api.post('/chat/send', {
          rideId,
          message: messageText
        });
        
        // Refresh messages
        fetchMessages();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error',
        description: 'Failed to send message',
        variant: 'destructive',
      });
      // Restore message text on error
      setNewMessage(messageText);
    }
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator
    if (ws && ws.readyState === WebSocket.OPEN) {
      // Throttle typing indicators
      if (!typingTimeoutRef.current) {
        sendTypingIndicator();
      }
    }
  };

  const sendTypingIndicator = async () => {
    try {
      const api = (await import('../utils/api')).default;
      await api.post('/chat/typing', { rideId });
    } catch (error) {
      // Silently fail for typing indicators
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setUnreadCount(0);
      setIsMinimized(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          onClick={toggleOpen}
          className="bg-pink-600 hover:bg-pink-700 text-white rounded-full shadow-lg h-14 w-14 relative"
        >
          <MessageSquare className="h-6 w-6" />
          {unreadCount > 0 && (
            <Badge className="absolute -top-2 -right-2 bg-red-500 text-white h-6 w-6 flex items-center justify-center rounded-full text-xs">
              {unreadCount}
            </Badge>
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-96">
      <Card className={`shadow-2xl border-pink-200 ${isMinimized ? 'h-16' : 'h-[500px]'} flex flex-col transition-all`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-pink-600 to-purple-600 text-white p-4 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <MessageSquare className="h-5 w-5" />
              </div>
              {isOnline && (
                <Circle className="absolute -bottom-1 -right-1 h-3 w-3 fill-green-400 text-green-400" />
              )}
            </div>
            <div>
              <h3 className="font-semibold">{otherUserName || 'Chat'}</h3>
              <p className="text-xs text-white/80">
                {isTyping ? 'typing...' : otherUserRole || 'User'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMinimized(!isMinimized)}
              className="text-white hover:bg-white/20 h-8 w-8 p-0"
            >
              {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleOpen}
              className="text-white hover:bg-white/20 h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
              {messages.length === 0 ? (
                <div className="text-center text-gray-500 mt-8">
                  <MessageSquare className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                  <p className="text-sm">No messages yet</p>
                  <p className="text-xs">Send a message to start the conversation</p>
                </div>
              ) : (
                messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.senderId === currentUserId ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-lg p-3 ${
                        msg.senderId === currentUserId
                          ? 'bg-pink-600 text-white'
                          : 'bg-white text-gray-900 border border-gray-200'
                      }`}
                    >
                      <p className="text-sm break-words">{msg.message}</p>
                      <p className={`text-xs mt-1 ${
                        msg.senderId === currentUserId ? 'text-pink-200' : 'text-gray-500'
                      }`}>
                        {formatTimestamp(msg.timestamp)}
                      </p>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 bg-white border-t">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  placeholder="Type a message..."
                  className="flex-1"
                />
                <Button
                  onClick={sendMessage}
                  disabled={!newMessage.trim()}
                  className="bg-pink-600 hover:bg-pink-700 text-white"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default ChatBox;
